import type { WeaponDefinition } from "@/types/game-data";

// --------------------------------------------------
// RIFLES
// --------------------------------------------------
export const RIFLES: readonly WeaponDefinition[] = [
	{
		id: "medium-rifle", name: "Medium Rifle", category: "rifle", cost: 10, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: "6+", ap: 1 },
			engagement: { range: '20"', modifier: 1, lethality: "5+", ap: 1 },
			extreme: { range: '40"', modifier: -1, lethality: "6+", ap: 1 },
			specialRules: [], examples: "G36, M4, AK47/74",
		},
	},
	{
		id: "short-rifle", name: "Short Rifle", category: "rifle", cost: 25, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "5+", ap: 1 },
			engagement: { range: '15"', modifier: 0, lethality: "6+", ap: 1 },
			extreme: { range: '30"', modifier: -1, lethality: "7+", ap: 0 },
			specialRules: ["Compact"], examples: "MK18, AKSU, AS VAL",
		},
	},
	{
		id: "long-rifle", name: "Long Rifle", category: "rifle", cost: 28, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "7+", ap: 1 },
			engagement: { range: '30"', modifier: 1, lethality: "6+", ap: 1 },
			extreme: { range: '50"', modifier: 1, lethality: "5+", ap: 1 },
			specialRules: [], examples: "M16",
		},
	},
	{
		id: "battle-rifle", name: "Battle Rifle", category: "rifle", cost: 30, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "4+", ap: 2 },
			engagement: { range: '20"', modifier: 1, lethality: "5+", ap: 1 },
			extreme: { range: '50"', modifier: -2, lethality: "6+", ap: 1 },
			specialRules: ["Armour Piercing"], examples: "FAL, SCAR H",
		},
	},
	{
		id: "bolt-action-rifle", name: "Bolt Action Rifle", category: "rifle", cost: 20, slot: "primary",
		notes: "Militia only", factions: ["militia"],
		stats: {
			cqb: { range: '6"', modifier: -2, lethality: "6+", ap: 1 },
			engagement: { range: '36"', modifier: null, lethality: "5+", ap: 1 },
			extreme: { range: "∞", modifier: -1, lethality: "6+", ap: 1 },
			specialRules: ["Single Fire"], examples: "Lee Enfield, M1 Springfield",
		},
	},
	{
		id: "pdw-rifle", name: "PDW Rifle", category: "rifle", cost: 28, slot: "primary",
		notes: "Task Force only", factions: ["taskforce", "russian"],
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "5+", ap: 1 },
			engagement: { range: '12"', modifier: -1, lethality: "6+", ap: 1 },
			extreme: { range: '20"', modifier: -2, lethality: "9+", ap: 0 },
			specialRules: ["Compact", "Covert"], examples: "MCX Rattler, HK416K",
		},
	},
	{
		id: "short-battle-rifle", name: "Short Battle Rifle", category: "rifle", cost: 25, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "4+", ap: 2 },
			engagement: { range: '15"', modifier: -1, lethality: "7+", ap: 1 },
			extreme: { range: '30"', modifier: -2, lethality: "8+", ap: 0 },
			specialRules: ["Armour Piercing", "Compact"], examples: "OSW",
		},
	},
	{
		id: "50-cal-rifle", name: ".50 Rifle", category: "rifle", cost: 35, slot: "primary",
		notes: "Task Force / Russian only", factions: ["taskforce", "russian"],
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "2+", ap: 2 },
			engagement: { range: '15"', modifier: 0, lethality: "4+", ap: 2 },
			extreme: { range: '30"', modifier: -1, lethality: "7+", ap: 1 },
			specialRules: ["Armour Piercing", "Breaching (+4)"], examples: "510 Grizzly, ShAK-12",
		},
	},
] as const;

// --------------------------------------------------
// SMGs
// --------------------------------------------------
export const SMGS: readonly WeaponDefinition[] = [
	{
		id: "compact-smg", name: "Compact SMG", category: "smg", cost: 20, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "7+", ap: 1 },
			engagement: { range: '12"', modifier: 0, lethality: "8+", ap: 1 },
			extreme: { range: '18"', modifier: -1, lethality: "10+", ap: 0 },
			specialRules: ["Automatic", "Compact", "Covert"], examples: "MP5K, MPX Copperhead, MP7, MP9, UZI",
		},
	},
	{
		id: "smg", name: "SMG", category: "smg", cost: 25, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "7+", ap: 1 },
			engagement: { range: '18"', modifier: 0, lethality: "8+", ap: 1 },
			extreme: { range: '24"', modifier: -2, lethality: "10+", ap: 0 },
			specialRules: ["Automatic", "Compact"], examples: "MP5, MPX",
		},
	},
] as const;

// --------------------------------------------------
// SHOTGUNS
// --------------------------------------------------
export const SHOTGUNS: readonly WeaponDefinition[] = [
	{
		id: "sawn-off-shotgun", name: "Sawn Off Shotgun", category: "shotgun", cost: 5, slot: "primary",
		factions: ["militia"],
		stats: {
			cqb: { range: '6"', modifier: 3, lethality: "4+", ap: 1 },
			engagement: { range: '8"', modifier: -1, lethality: "8+", ap: 0 },
			extreme: { range: '12"', modifier: -2, lethality: "10+", ap: 0 },
			specialRules: ["Compact", "Covert", "Single Fire"],
		},
	},
	{
		id: "shotgun", name: "Shotgun", category: "shotgun", cost: 12, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "4+", ap: 1 },
			engagement: { range: '10"', modifier: 0, lethality: "7+", ap: 0 },
			extreme: { range: '16"', modifier: 0, lethality: "9+", ap: 0 },
			specialRules: ["Single Fire", "Breaching (+1)"],
		},
	},
	{
		id: "combat-shotgun", name: "Combat Shotgun", category: "shotgun", cost: 16, slot: "primary",
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "4+", ap: 1 },
			engagement: { range: '12"', modifier: 0, lethality: "7+", ap: 1 },
			extreme: { range: '20"', modifier: 0, lethality: "8+", ap: 0 },
			specialRules: ["Breaching (+2)"], examples: "TS12, Benelli M4",
		},
	},
	{
		id: "automatic-shotgun", name: "Automatic Shotgun", category: "shotgun", cost: 24, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "4+", ap: 1 },
			engagement: { range: '10"', modifier: 0, lethality: "7+", ap: 1 },
			extreme: { range: '16"', modifier: 0, lethality: "8+", ap: 0 },
			specialRules: ["Automatic", "Suppressing", "Breaching (+2)"], examples: "AA-12, Origin 12, Gen 12",
		},
	},
	{
		id: "shotgun-slug-ammo", name: "Additional Shotgun Ammo: Slug", category: "shotgun", cost: 10, slot: "primary",
		notes: "Ammo upgrade",
	},
	{
		id: "shotgun-flechette-ammo", name: "Additional Shotgun Ammo: Flechette", category: "shotgun", cost: 15, slot: "primary",
		notes: "Task Force only, ammo upgrade", factions: ["taskforce"],
	},
] as const;

// --------------------------------------------------
// SIDEARMS
// --------------------------------------------------
export const SIDEARMS: readonly WeaponDefinition[] = [
	{
		id: "pistol", name: "Pistol", category: "sidearm", cost: 5, slot: "sidearm",
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "7+", ap: 1 },
			engagement: { range: '10"', modifier: 0, lethality: "8+", ap: 1 },
			extreme: { range: '16"', modifier: -2, lethality: "10+", ap: 0 },
			specialRules: ["Compact", "Covert"], examples: "Glock, 1911",
		},
	},
	{
		id: "heavy-pistol", name: "Heavy Pistol", category: "sidearm", cost: 8, slot: "sidearm",
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: "4+", ap: 1 },
			engagement: { range: '10"', modifier: -1, lethality: "7+", ap: 1 },
			extreme: { range: '16"', modifier: -3, lethality: "8+", ap: 0 },
			specialRules: ["Compact"], examples: ".45 Magnum, Desert Eagle, RSh-12",
		},
	},
	{
		id: "machine-pistol", name: "Machine Pistol", category: "sidearm", cost: 10, slot: "sidearm",
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "6+", ap: 1 },
			engagement: { range: '8"', modifier: -1, lethality: "9+", ap: 1 },
			extreme: { range: '16"', modifier: -3, lethality: "10+", ap: 0 },
			specialRules: ["Automatic", "Compact", "Covert"], examples: "Mac10, UZI pro, PP2000",
		},
	},
	{
		id: "integrally-suppressed-pistol", name: "Integrally Suppressed Pistol", category: "sidearm", cost: 15, slot: "sidearm",
		notes: "Task Force / Russian SSO only", factions: ["taskforce", "russian"],
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "7+", ap: 1 },
			engagement: { range: '10"', modifier: -1, lethality: "9+", ap: 0 },
			extreme: { range: '16"', modifier: -2, lethality: "10+", ap: 0 },
			specialRules: ["Silent", "Compact", "Covert", "Integrally Suppressed"], examples: "VP9",
		},
	},
	{
		id: "ranged-taser", name: "Ranged Taser", category: "sidearm", cost: 10, slot: "sidearm",
		notes: "Task Force only", factions: ["taskforce"],
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: null, ap: 0 },
			engagement: { range: '8"', modifier: -1, lethality: null, ap: 0 },
			extreme: { range: "-", modifier: null, lethality: null, ap: null },
			specialRules: ["Non Lethal", "Stun 1", "Silent"],
		},
	},
] as const;

// --------------------------------------------------
// MARKSMAN RIFLES
// --------------------------------------------------
export const MARKSMAN_RIFLES: readonly WeaponDefinition[] = [
	{
		id: "dmr", name: "DMR", category: "marksman", cost: 30, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "7+", ap: 1 },
			engagement: { range: '36"', modifier: 1, lethality: "5+", ap: 1 },
			extreme: { range: "∞", modifier: 1, lethality: "5+", ap: 1 },
			specialRules: ["Marksman", "Armour Piercing"], examples: "HK417, M110",
		},
	},
	{
		id: "medium-sniper", name: "Medium Sniper", category: "marksman", cost: 40, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: -3, lethality: "7+", ap: 1 },
			engagement: { range: '42"', modifier: 2, lethality: "5+", ap: 1 },
			extreme: { range: "∞", modifier: 1, lethality: "5+", ap: 1 },
			specialRules: ["Marksman", "Armour Piercing", "Suppressing", "Single Fire", "Heavy"], examples: "SVD, SSG 69, M24",
		},
	},
	{
		id: "heavy-sniper", name: "Heavy Sniper", category: "marksman", cost: 50, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: -4, lethality: "8+", ap: 1 },
			engagement: { range: '48"', modifier: 2, lethality: "4+", ap: 2 },
			extreme: { range: "∞", modifier: 1, lethality: "3+", ap: 2 },
			specialRules: ["Marksman", "Anti Armour", "Single Fire", "Suppressing", "Heavy"], examples: "Arctic Warfare, MRAD",
		},
	},
	{
		id: "anti-material-rifle", name: "Anti-Material Rifle", category: "marksman", cost: 75, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: -4, lethality: "8+", ap: 1 },
			engagement: { range: '60"', modifier: 2, lethality: "3+", ap: 2 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 2 },
			specialRules: ["Marksman", "Anti Armour", "Single Fire", "Frag", "Suppressing", "Heavy"], examples: "M107, KSVK, GM6 Lynx",
		},
	},
	{
		id: "payload-rifle", name: "Payload Rifle", category: "marksman", cost: 90, slot: "specialist",
		notes: "Task Force only", factions: ["taskforce"],
		stats: {
			cqb: { range: '6"', modifier: -4, lethality: "8+", ap: 1 },
			engagement: { range: '70"', modifier: 1, lethality: "2+", ap: 2 },
			extreme: { range: "∞", modifier: 0, lethality: "2+", ap: 2 },
			specialRules: ["Marksman", "AoE 2", "Anti Armour", "Single Fire", "Frag", "Suppressing", "Heavy"], examples: "XM109",
		},
	},
] as const;

// --------------------------------------------------
// MACHINE GUNS
// --------------------------------------------------
export const MACHINE_GUNS: readonly WeaponDefinition[] = [
	{
		id: "assault-lmg", name: "Assault LMG", category: "machinegun", cost: 24, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "5+", ap: 1 },
			engagement: { range: '15"', modifier: 1, lethality: "6+", ap: 1 },
			extreme: { range: '25"', modifier: -1, lethality: "7+", ap: 0 },
			specialRules: ["Automatic", "Compact"], examples: "RPK, Minimi Para, LAMG, Evolys",
		},
	},
	{
		id: "lmg", name: "LMG", category: "machinegun", cost: 30, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: "6+", ap: 1 },
			engagement: { range: '20"', modifier: 1, lethality: "5+", ap: 1 },
			extreme: { range: '40"', modifier: -1, lethality: "6+", ap: 1 },
			specialRules: ["Automatic", "Suppressing"], examples: "SAW, M249, PKM/PKP",
		},
	},
	{
		id: "mmg", name: "MMG", category: "machinegun", cost: 35, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "7+", ap: 1 },
			engagement: { range: '30"', modifier: 1, lethality: "5+", ap: 2 },
			extreme: { range: '50"', modifier: 0, lethality: "5+", ap: 1 },
			specialRules: ["Sustained 2", "Suppressing", "Heavy"], examples: "M60, M240",
		},
	},
	{
		id: "pack-fed-lmg", name: "Pack Fed LMG", category: "machinegun", cost: 35, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: "6+", ap: 1 },
			engagement: { range: '20"', modifier: 1, lethality: "5+", ap: 1 },
			extreme: { range: '40"', modifier: -1, lethality: "6+", ap: 1 },
			specialRules: ["Sustained 2", "Suppressing"], examples: "PKP Skorpion",
		},
	},
	{
		id: "medium-machine-gun", name: "Medium Machine Gun", category: "machinegun", cost: 38, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "7+", ap: 1 },
			engagement: { range: '30"', modifier: 1, lethality: "5+", ap: 2 },
			extreme: { range: '50"', modifier: 0, lethality: "5+", ap: 1 },
			specialRules: ["Sustained 2", "Suppressing", "Heavy"], examples: "M60, M240",
		},
	},
	{
		id: "308-mg-assault-mg", name: "308 MG Assault MG", category: "machinegun", cost: 45, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "7+", ap: 2 },
			engagement: { range: '40"', modifier: 1, lethality: "4+", ap: 2 },
			extreme: { range: '60"', modifier: 0, lethality: "5+", ap: 1 },
			specialRules: ["Sustained 2", "Suppressing", "Armour Piercing"], examples: "Sig 308, M60E6",
		},
	},
	{
		id: "hmg", name: "HMG", category: "machinegun", cost: 50, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: -2, lethality: "5+", ap: 2 },
			engagement: { range: '50"', modifier: 1, lethality: "3+", ap: 2 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 2 },
			specialRules: ["Sustained 2", "Suppressing", "Armour Piercing", "Heavy", "Deployed"], examples: "M2, DShK, Kord",
		},
	},
	{
		id: "microgun", name: "Microgun", category: "machinegun", cost: 75, slot: "specialist",
		notes: "Task Force only", factions: ["taskforce"],
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "6+", ap: 1 },
			engagement: { range: '20"', modifier: 1, lethality: "7+", ap: 1 },
			extreme: { range: '40"', modifier: 0, lethality: "8+", ap: 1 },
			specialRules: ["Sustained 3", "Suppressing"], examples: "XM556",
		},
	},
] as const;

// --------------------------------------------------
// LAUNCHERS
// --------------------------------------------------
export const LAUNCHERS: readonly WeaponDefinition[] = [
	{
		id: "grenade-launcher", name: "Grenade Launcher", category: "launcher", cost: 25, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '20"', modifier: -1, lethality: "6+", ap: 1 },
			extreme: { range: '40"', modifier: -2, lethality: "6+", ap: 1 },
			specialRules: ["AoE 3", "Compact", "Single Fire", "Frag", "Suppressing", "Armour Piercing", "Minimum Range 8\""], examples: "M320, M79, Rifle UGL",
		},
	},
	{
		id: "rpg-heat", name: "RPG HEAT", category: "launcher", cost: 28, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '30"', modifier: -1, lethality: "8+", ap: 2 },
			extreme: { range: '50"', modifier: -2, lethality: "8+", ap: 2 },
			specialRules: ["AoE 2", "Anti Armour", "Single Fire", "Minimum Range 8\""], examples: "RPG 7",
		},
	},
	{
		id: "rpg-frag", name: "RPG Frag", category: "launcher", cost: 36, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '30"', modifier: -1, lethality: "6+", ap: 2 },
			extreme: { range: '50"', modifier: -2, lethality: "6+", ap: 1 },
			specialRules: ["AoE 4", "Frag", "Single Fire", "Minimum Range 8\""],
		},
	},
	{
		id: "assault-grenade-launcher", name: "Assault Grenade Launcher", category: "launcher", cost: 35, slot: "specialist",
		notes: "Task Force / Russian SSO only", factions: ["taskforce", "russian"],
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '14"', modifier: -1, lethality: "6+", ap: 1 },
			extreme: { range: '24"', modifier: -2, lethality: "6+", ap: 1 },
			specialRules: ["AoE 3", "Compact", "Single Fire", "Frag", "Suppressing", "Armour Piercing", "Minimum Range 8\""], examples: "GM 94",
		},
	},
	{
		id: "light-recoilless-rifle", name: "Light Recoilless Rifle (HEAT, Airburst, Flechette)", category: "launcher", cost: 40, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '35"', modifier: 0, lethality: "8+", ap: 2 },
			extreme: { range: '55"', modifier: -1, lethality: "8+", ap: 2 },
			specialRules: ["AoE 2", "Anti Armour", "Single Fire", "Minimum Range 8\""], examples: "Carl Gustav",
		},
	},
	{
		id: "rpg-thermobaric", name: "RPG Thermobaric", category: "launcher", cost: 45, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '30"', modifier: -1, lethality: "4+", ap: 2 },
			extreme: { range: '50"', modifier: -2, lethality: "4+", ap: 2 },
			specialRules: ["AoE 4", "Thermobaric", "Single Fire", "Armour Piercing", "Minimum Range 8\""],
		},
	},
	{
		id: "multi-grenade-launcher", name: "Multi-Grenade Launcher", category: "launcher", cost: 50, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '20"', modifier: -1, lethality: "6+", ap: 1 },
			extreme: { range: '40"', modifier: -2, lethality: "6+", ap: 1 },
			specialRules: ["AoE 3", "Automatic", "Frag", "Suppressing", "Armour Piercing", "Minimum Range 8\""], examples: "SSW40, Milkor",
		},
	},
	{
		id: "smart-grenade-launcher", name: "Smart Grenade Launcher", category: "launcher", cost: 50, slot: "specialist",
		notes: "Task Force only", factions: ["taskforce"],
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "5+", ap: 1 },
			engagement: { range: '30"', modifier: -1, lethality: "5+", ap: 1 },
			extreme: { range: '60"', modifier: -2, lethality: "5+", ap: 1 },
			specialRules: ["AoE 3", "Compact", "Single Fire", "Frag", "Suppressing", "Anti Armour", "Airburst", "Guided"], examples: "Pike",
		},
	},
	{
		id: "at4-heat-he", name: "AT4 HEAT/HE", category: "launcher", cost: 60, slot: "specialist",
		notes: "Task Force only", factions: ["taskforce"],
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '35"', modifier: 0, lethality: "8+", ap: 4 },
			extreme: { range: '55"', modifier: -1, lethality: "8+", ap: 4 },
			specialRules: ["AoE 2", "Anti Armour", "Single Fire", "Heavy", "Tandem Warhead", "Minimum Range 8\""],
		},
	},
	{
		id: "rpg-tandem-warhead", name: "RPG Tandem Warhead", category: "launcher", cost: 70, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '30"', modifier: -1, lethality: "8+", ap: 2 },
			extreme: { range: '50"', modifier: -2, lethality: "8+", ap: 3 },
			specialRules: ["AoE 2", "Anti Armour", "Tandem Warhead", "Single Fire", "Heavy", "Minimum Range 8\""],
		},
	},
	{
		id: "unguided-anti-tank-launcher", name: "Unguided Anti Tank Launcher", category: "launcher", cost: 80, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '35"', modifier: -1, lethality: "6+", ap: 6 },
			extreme: { range: '55"', modifier: -2, lethality: "6+", ap: 6 },
			specialRules: ["AoE 2", "Anti Tank", "One Shot", "Heavy", "Tandem Warhead", "Minimum Range 8\""], examples: "RPG 29, RPG 30",
		},
	},
	{
		id: "light-guided-anti-tank-launcher", name: "Light Guided Anti-Tank Launcher", category: "launcher", cost: 120, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '35"', modifier: null, lethality: "5+", ap: 8 },
			extreme: { range: '60"', modifier: null, lethality: "5+", ap: 8 },
			specialRules: ["AoE 2", "Anti Tank", "One Shot", "Heavy", "Tandem Warhead", "Guided", "Top Down", "Minimum Range 10\""], examples: "NLAW, Kornet, TOW",
		},
	},
	{
		id: "javelin", name: "Javelin", category: "launcher", cost: 150, slot: "specialist",
		notes: "Task Force only", factions: ["taskforce"],
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '40"', modifier: null, lethality: "5+", ap: 8 },
			extreme: { range: "∞", modifier: null, lethality: "5+", ap: 8 },
			specialRules: ["AoE 2", "Anti Tank", "One Shot", "Heavy", "Tandem Warhead", "Guided", "Top Down", "Minimum Range 20\""],
		},
	},
	// Russian expansion launchers
	{
		id: "rpg-22-26", name: "RPG 22/26", category: "launcher", cost: 40, slot: "specialist",
		notes: "Anti Armour, Single Fire", factions: ["russian"],
	},
	{
		id: "rpo-shimel", name: "RPO Shimel", category: "launcher", cost: 80, slot: "specialist",
		notes: "AoE 5, Thermobaric, Single Fire", factions: ["russian"],
	},
	{
		id: "rpg-30", name: "RPG-30", category: "launcher", cost: 100, slot: "specialist",
		notes: "Tandem Warhead, ERA Defeat", factions: ["russian"],
	},
	{
		id: "bur", name: "BUR", category: "launcher", cost: 40, slot: "specialist",
		notes: "AoE 4, Thermobaric, Compact", factions: ["russian"],
	},
	// Law Enforcement launcher
	{
		id: "grenade-launcher-cs-gas", name: "Grenade Launcher (CS Gas)", category: "launcher", cost: 25, slot: "specialist",
		notes: "AoE 5, Stun 2, CS Gas", factions: ["law-enforcement", "criminal-element"],
	},
] as const;

// --------------------------------------------------
// CLOSE COMBAT
// --------------------------------------------------
export const CLOSE_COMBAT: readonly WeaponDefinition[] = [
	{
		id: "close-combat-weapon", name: "Close Combat Weapon", category: "closecombat", cost: 5, slot: "closecombat",
		assaultStats: { range: "-", lethality: "6+", ap: null, specialRules: ["Covert", "Armour Piercing", "Silent"] },
	},
	{
		id: "specialist-melee-weapon", name: "Specialist Weapon", category: "closecombat", cost: 15, slot: "closecombat",
		assaultStats: { range: '1"', lethality: "5+", ap: null, specialRules: ["Armour Piercing"] },
	},
	{
		id: "taser", name: "Taser", category: "closecombat", cost: 5, slot: "closecombat",
		assaultStats: { range: "-", lethality: null, ap: 0, specialRules: ["Covert", "Non Lethal", "Stun 1", "Silent"] },
	},
] as const;

// --------------------------------------------------
// THROWN WEAPONS
// --------------------------------------------------
export const THROWN_WEAPONS: readonly WeaponDefinition[] = [
	{
		id: "frag-grenade", name: "Frag Grenade", category: "thrown", cost: 30, slot: "thrown",
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: "6+", ap: 1 },
			engagement: { range: '8"', modifier: -1, lethality: "6+", ap: 1 },
			extreme: { range: '12"', modifier: -2, lethality: "6+", ap: 1 },
			specialRules: ["AoE 3", "Frag", "One Shot"],
		},
	},
	{
		id: "mini-frag-grenade", name: "Mini Frag Grenade", category: "thrown", cost: 10, slot: "thrown",
		notes: "Task Force only", factions: ["taskforce"],
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: "6+", ap: 1 },
			engagement: { range: '10"', modifier: 0, lethality: "6+", ap: 1 },
			extreme: { range: '14"', modifier: -2, lethality: "6+", ap: 1 },
			specialRules: ["AoE 2", "Frag", "One Shot"],
		},
	},
	{
		id: "molotov", name: "Molotov Cocktail", category: "thrown", cost: 20, slot: "thrown",
		factions: ["militia"],
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "7+", ap: 1 },
			engagement: { range: '8"', modifier: -2, lethality: "7+", ap: 1 },
			extreme: { range: '10"', modifier: -3, lethality: "7+", ap: 1 },
			specialRules: ["AoE 2", "Incendiary", "One Shot"],
		},
	},
	{
		id: "flash-grenade", name: "Flash Grenade", category: "thrown", cost: 15, slot: "thrown",
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: null, ap: 0 },
			engagement: { range: '6"', modifier: 0, lethality: null, ap: 0 },
			extreme: { range: '6"', modifier: 0, lethality: null, ap: 0 },
			specialRules: ["AoE 6", "Stun 1", "One Shot"],
		},
	},
	{
		id: "smoke-grenade", name: "Smoke Grenade", category: "thrown", cost: 5, slot: "thrown",
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: null, ap: 1 },
			engagement: { range: '8"', modifier: -1, lethality: null, ap: 1 },
			extreme: { range: '12"', modifier: -2, lethality: null, ap: 1 },
			specialRules: ["Smoke", "Silent", "One Shot"],
		},
	},
	{
		id: "ir-marker-smoke", name: "IR/Marker Smoke/CS", category: "thrown", cost: 10, slot: "thrown",
		notes: "Task Force only, 10r each type", factions: ["taskforce"],
	},
	{
		id: "ir-smoke", name: "IR Smoke", category: "thrown", cost: 15, slot: "thrown",
		factions: ["russian", "law-enforcement"],
	},
	{
		id: "marker-smoke", name: "Marker Smoke", category: "thrown", cost: 5, slot: "thrown",
		factions: ["russian"],
	},
	{
		id: "incendiary-grenade", name: "Incendiary Grenade", category: "thrown", cost: 15, slot: "thrown",
		factions: ["russian"],
	},
	{
		id: "gas-grenade", name: "Gas Grenade", category: "thrown", cost: 15, slot: "thrown",
		factions: ["russian", "law-enforcement"],
	},
] as const;

// --------------------------------------------------
// FACTION-SPECIFIC WEAPONS (different pricing)
// --------------------------------------------------
// These weapons have the same name as base weapons but different costs
// for specific factions. The ModelEditor deduplicates by name, preferring
// faction-tagged versions.
export const FACTION_SPECIFIC_WEAPONS: readonly WeaponDefinition[] = [
	// Criminal Element — higher cost due to black-market sourcing
	{
		id: "criminal-medium-rifle",
		name: "Medium Rifle",
		category: "rifle",
		cost: 20,
		slot: "primary",
		factions: ["criminal-element"],
	},
	{
		id: "criminal-short-rifle",
		name: "Short Rifle",
		category: "rifle",
		cost: 30,
		slot: "primary",
		notes: "Crime Boss pricing",
		factions: ["criminal-element"],
	},
	{
		id: "criminal-machine-pistol",
		name: "Machine Pistol",
		category: "sidearm",
		cost: 15,
		slot: "sidearm",
		factions: ["criminal-element"],
	},
	{
		id: "criminal-sawn-off",
		name: "Sawn Off Shotgun",
		category: "shotgun",
		cost: 8,
		slot: "primary",
		factions: ["criminal-element"],
	},
	{
		id: "criminal-frag-grenade",
		name: "Frag Grenade",
		category: "thrown",
		cost: 30,
		slot: "thrown",
		factions: ["criminal-element"],
	},
	{
		id: "criminal-molotov",
		name: "Molotov Cocktail",
		category: "thrown",
		cost: 20,
		slot: "thrown",
		factions: ["criminal-element"],
	},
	// Law Enforcement — discounted rifles (government procurement)
	{
		id: "le-pdw-rifle",
		name: "PDW Rifle",
		category: "rifle",
		cost: 18,
		slot: "primary",
		factions: ["law-enforcement"],
	},
	{
		id: "le-short-rifle",
		name: "Short Rifle",
		category: "rifle",
		cost: 15,
		slot: "primary",
		factions: ["law-enforcement"],
	},
	{
		id: "le-long-rifle",
		name: "Long Rifle",
		category: "rifle",
		cost: 18,
		slot: "primary",
		factions: ["law-enforcement"],
	},
] as const;

// --------------------------------------------------
// SPECIALIST ARMOURY
// --------------------------------------------------
export const SPECIALIST_WEAPONS: readonly WeaponDefinition[] = [
	{
		id: "flamethrower", name: "Flamethrower", category: "specialist", cost: 60, slot: "specialist",
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "4+", ap: 1 },
			engagement: { range: '10"', modifier: 1, lethality: "5+", ap: 1 },
			extreme: { range: '16"', modifier: -2, lethality: "9+", ap: 0 },
			specialRules: ["AoE 3", "Single Fire", "Incendiary", "Suppressing"],
		},
	},
	{
		id: "anti-drone-gun", name: "Anti-Drone Gun", category: "specialist", cost: 50, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: 1, lethality: null, ap: null },
			engagement: { range: '18"', modifier: -1, lethality: null, ap: null },
			extreme: { range: '30"', modifier: -2, lethality: null, ap: null },
			specialRules: ["Anti-Drone", "Silent"],
		},
	},
	{
		id: "anti-personnel-armour-drone", name: "Anti-Personnel/Armour Drone", category: "specialist", cost: 50, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: "5+", ap: 1 },
			engagement: { range: "-", modifier: null, lethality: "5+", ap: 1 },
			extreme: { range: "-", modifier: null, lethality: "5+", ap: 1 },
			specialRules: ["AoE 5", "One Shot", "Frag", "Guided"],
		},
	},
	{
		id: "personal-defence-weapon", name: "Personal Defence Weapon", category: "specialist", cost: 5, slot: "specialist",
		assaultStats: { range: "-", lethality: "8+", ap: null, specialRules: ["One Shot"] },
	},
] as const;

// --------------------------------------------------
// EMPLACED WEAPONS
// --------------------------------------------------
export const EMPLACED_WEAPONS: readonly WeaponDefinition[] = [
	{
		id: "spg9-heat-he", name: "SPG9 HEAT/HE", category: "emplaced", cost: 90, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '35"', modifier: 0, lethality: "7+", ap: 2 },
			extreme: { range: '60"', modifier: -1, lethality: "7+", ap: 2 },
			specialRules: ["AoE 2", "Anti Armour", "Single Fire", "Minimum Range 10\"", "Deployed"],
		},
	},
	{
		id: "m40-heat", name: "M40 HEAT", category: "emplaced", cost: 120, slot: "specialist",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '45"', modifier: 0, lethality: "7+", ap: 2 },
			extreme: { range: "∞", modifier: -2, lethality: "7+", ap: 3 },
			specialRules: ["AoE 2", "Anti Armour", "Single Fire", "Minimum Range 10\"", "Deployed"],
		},
	},
] as const;

// --------------------------------------------------
// ALL WEAPONS combined
// --------------------------------------------------
export const ALL_WEAPONS: readonly WeaponDefinition[] = [
	...RIFLES,
	...SMGS,
	...SHOTGUNS,
	...SIDEARMS,
	...MARKSMAN_RIFLES,
	...MACHINE_GUNS,
	...LAUNCHERS,
	...CLOSE_COMBAT,
	...THROWN_WEAPONS,
	...SPECIALIST_WEAPONS,
	...EMPLACED_WEAPONS,
	...FACTION_SPECIFIC_WEAPONS,
] as const;

/** Lookup weapon by ID */
export function getWeaponById(id: string): WeaponDefinition | undefined {
	return ALL_WEAPONS.find((w) => w.id === id);
}

/** Get weapons by category */
export function getWeaponsByCategory(category: string): readonly WeaponDefinition[] {
	return ALL_WEAPONS.filter((w) => w.category === category);
}

/** Get weapons available for a specific slot */
export function getWeaponsBySlot(slot: string): readonly WeaponDefinition[] {
	return ALL_WEAPONS.filter((w) => w.slot === slot);
}

/**
 * Filter weapons for a specific faction, deduplicating by name.
 * When a faction-tagged weapon shares a name with an untagged base weapon,
 * the faction-specific version wins.
 */
export function getWeaponsForFaction(
	weapons: readonly WeaponDefinition[],
	faction: string,
): WeaponDefinition[] {
	// Group by display name
	const byName = new Map<string, WeaponDefinition[]>();
	for (const w of weapons) {
		const list = byName.get(w.name) ?? [];
		list.push(w);
		byName.set(w.name, list);
	}

	const result: WeaponDefinition[] = [];
	for (const [, variants] of byName) {
		// Look for a faction-specific variant
		const factionMatch = variants.find(
			(v) => v.factions !== undefined && v.factions.includes(faction as never),
		);
		if (factionMatch) {
			result.push(factionMatch);
			continue;
		}

		// Otherwise, include base weapons (no factions restriction)
		for (const v of variants) {
			if (v.factions === undefined) {
				result.push(v);
			}
		}
	}

	return result;
}
